import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Paper,
  Button,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Grid,
  CircularProgress
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SubjectCard from './SubjectCard';
import SubjectListCard from './SubjectListCard';
import { Http } from 'api/http/http';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from 'store/reducers/auth';

/** /api/courses/progress 응답을 그대로 정규화 (isCertificateAvailable 유지) */
const processModuleData = (allCoursesData) =>
  allCoursesData.map((levelData) => ({
    ...levelData,
    modules: levelData.modules.map((module) => ({
      ...module,
      standardCourses: module.standardCourses.map((standardCourse) => ({
        ...standardCourse,
        courses: standardCourse.courses.map((course) => ({
          ...course
          // isCertificateAvailable: 그대로 유지
        }))
      }))
    }))
  }));

/** 모듈 단위 카드 뷰용 데이터 생성 */
const getModuleViewData = (processedData) => {
  if (!processedData || !Array.isArray(processedData)) return [];

  const levelLabelMap = {
    초급: '초급 과정',
    중급: '중급 과정',
    고급: '고급 과정'
  };

  const modules = [];

  processedData.forEach((levelData) => {
    if (!levelData || !levelData.level || !Array.isArray(levelData.modules)) return;

    const levelLabel = levelLabelMap[levelData.level] || levelData.level;

    levelData.modules.forEach((module) => {
      if (!module || !module.moduleName || !Array.isArray(module.standardCourses)) return;

      const standardCourses = module.standardCourses
        .filter(Boolean)
        .map((standardCourse) => {
          if (!standardCourse || !Array.isArray(standardCourse.courses)) return null;

          const lectureOptions = standardCourse.courses.filter(Boolean).map((course) => ({
            courseId: course.courseId,
            courseName: course.courseName,
            credit: typeof course.credit === 'number' ? course.credit : 3,
            isSelectable: !!course.isCertificateAvailable
          }));

          if (lectureOptions.length === 0) return null;

          return {
            standardName: standardCourse.standardCourseName,
            lectureOptions
          };
        })
        .filter(Boolean);

      if (standardCourses.length === 0) return;

      modules.push({
        moduleName: module.moduleName,
        levelLabel,
        isApproved: !!module.isApproved,
        standardCourses
      });
    });
  });

  return modules;
};

const MyTable = () => {
  const [selected, setSelected] = useState({ selectedLectures: [], module: '', changed: false, credit: 0 });
  const [processedData, setProcessedData] = useState(null);
  const [applyingModules, setApplyingModules] = useState([]); // 신청 중 모듈
  const [moduleViewData, setModuleViewData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleRefreshExpired = () => {
    navigate('/studentLogin');
  };

  const http = new Http({ onRefreshExpired: handleRefreshExpired, onLogout: handleLogout });

  const { major } = useSelector((state) => state.auth);
  const isMajorStem = major === 'STEM';

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // progress + certificates만 사용
      const [allCoursesResponse, certificatesResponse] = await Promise.all([
        http.getWithToken('/api/courses/progress'),
        http.getWithToken('/api/certificates')
      ]);

      const allCoursesData = allCoursesResponse.data.result;
      const certificatesData = certificatesResponse.data.result;

      // 신청 중(PENDING) / 승인(APPROVED) 모듈 상태
      const pendingModules = certificatesData.filter((cert) => cert.status === 'PENDING').map((cert) => cert.moduleName);
      setApplyingModules(pendingModules);

      const approvedModules = certificatesData.filter((cert) => cert.status === 'APPROVED').map((cert) => cert.moduleName);

      // 데이터 정규화
      const processed = processModuleData(allCoursesData);

      // 모듈 승인 플래그 주입
      const processedWithApproved = processed.map((levelData) => ({
        ...levelData,
        modules: levelData.modules.map((module) => ({
          ...module,
          isApproved: approvedModules.includes(module.moduleName)
        }))
      }));

      setProcessedData(processedWithApproved);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('데이터를 불러오는데 오류가 발생했습니다. 다시 시도해주세요');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (processedData !== null) {
      const modules = getModuleViewData(processedData);
      setModuleViewData(modules);
    }
  }, [processedData]);

  const requestIssueHandler = async (moduleName) => {
    // 1) 12학점 이상
    if (selected.credit < 12) {
      // eslint-disable-next-line no-alert
      alert('12학점 이상만 신청할 수 있습니다.');
      return;
    }
    // 2) 신청 중 모듈
    if (applyingModules.includes(moduleName)) {
      // eslint-disable-next-line no-alert
      alert('이미 신청 중인 모듈입니다. 신청이 불가능합니다.');
      return;
    }
    // 3) 승인된 모듈
    const isModuleApproved = processedData?.some((levelData) =>
      levelData.modules.some((module) => module.moduleName === moduleName && module.isApproved)
    );
    if (isModuleApproved) {
      // eslint-disable-next-line no-alert
      alert('이미 승인된 모듈입니다. 신청이 불가능합니다.');
      return;
    }
    // 4) 선택한 모듈과 일치
    if (selected.module !== moduleName) {
      // eslint-disable-next-line no-alert
      alert('선택한 과목의 모듈에 발급신청을 해주세요.');
      return;
    }

    // 선택된 교과목 courseId 수집 (이제 selectedLectures는 {courseId, courseName})
    const selectedCourseIds = selected.selectedLectures.map((lec) => (typeof lec === 'object' ? lec.courseId : null)).filter(Boolean);

    const moduleToCertificate = {
      moduleName: selected.module,
      courseIds: selectedCourseIds
    };

    try {
      const response = await http.postWithToken('/api/certificates', moduleToCertificate);
      if (response.status === 200) {
        // eslint-disable-next-line no-alert
        alert('신청이 완료되었습니다');
      } else {
        // eslint-disable-next-line no-alert
        alert('신청에 실패하였습니다. 시스템 관리자에게 문의해주세요');
      }
      setSelected({ selectedLectures: [], module: '', changed: false, credit: 0 });
      handleModalOpen();
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('신청에 실패하였습니다. 다시 시도해주세요.');
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    navigate('/student-certificate-management');
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* 상단 요약/안내 영역 (sticky header) */}
      <Paper
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          p: 2,
          mb: 1.5,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          bgcolor: 'background.paper',
          boxShadow: 1
        }}
      >
        <Typography variant="h6">교과목 이수현황</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2">선택 모듈: {selected.module || '없음'}</Typography>
          <Typography variant="body2">선택 학점: {selected.credit}학점</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" color="text.secondary">
              각 모듈 카드 안에서 교과목을 선택하고, 발급신청 버튼을 눌러 이수증을 신청하세요.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* 모듈 카드 리스트 */}
      <Box sx={{ mt: 1 }}>
        {moduleViewData && moduleViewData.length > 0 ? (
          <Grid container spacing={2}>
            {moduleViewData.map((module) => (
              <Grid item xs={12} sm={12} md={6} lg={4} key={module.moduleName}>
                <Paper
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        label={(module.levelLabel || '').replace(' 과정', '')}
                        color={
                          (module.levelLabel || '').includes('초급')
                            ? 'success'
                            : (module.levelLabel || '').includes('중급')
                              ? 'warning'
                              : 'error'
                        }
                      />
                      <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {module.moduleName}
                        {module.isApproved && (
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', color: 'success.dark' }}>
                            <CheckCircleOutlineIcon fontSize="small" />
                          </Box>
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      {isMajorStem ? (
                        module.moduleName === '비이공계' ? (
                          <Button sx={{ ml: 1 }} variant="contained" disabled>
                            비이공계열만 신청가능
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={() => requestIssueHandler(module.moduleName)}
                            disabled={applyingModules.includes(module.moduleName) || module.isApproved}
                          >
                            {applyingModules.includes(module.moduleName) ? '신청 중' : module.isApproved ? '승인 완료' : '발급신청'}
                          </Button>
                        )
                      ) : module.moduleName === '이공계' ? (
                        <Button sx={{ ml: 1 }} variant="contained" disabled>
                          이공계열만 신청가능
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={() => requestIssueHandler(module.moduleName)}
                          disabled={applyingModules.includes(module.moduleName) || module.isApproved}
                        >
                          {applyingModules.includes(module.moduleName) ? '신청 중' : module.isApproved ? '승인 완료' : '발급신청'}
                        </Button>
                      )}

                      {/* 상태/제한 사유 설명 텍스트 */}
                      {module.isApproved && (
                        <Typography variant="caption" color="success.dark" display="block" sx={{ mt: 0.5 }}>
                          이수 승인되었습니다. 축하합니다!
                        </Typography>
                      )}
                      {applyingModules.includes(module.moduleName) && !module.isApproved && (
                        <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" sx={{ mt: 0.5, gap: 0.5 }}>
                          <CircularProgress size={10} thickness={6} />
                          현재 심사 중입니다.
                        </Typography>
                      )}
                      {isMajorStem && module.moduleName === '비이공계' && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          이공계 학생은 신청할 수 없습니다.
                        </Typography>
                      )}
                      {!isMajorStem && module.moduleName === '이공계' && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          비이공계 학생은 신청할 수 없습니다.
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {module.moduleName === selected.module && selected.credit !== 0 && (
                    <Typography variant="body2" color="primary">
                      {selected.credit}학점 선택
                    </Typography>
                  )}

                  <Grid container spacing={1}>
                    {module.standardCourses.map((standardCourse) => {
                      const hasMultipleOptions = standardCourse.lectureOptions.length > 1;
                      return (
                        <Grid item xs={12} md={6} key={`${module.moduleName}-${standardCourse.standardName}`}>
                          {hasMultipleOptions ? (
                            <SubjectListCard
                              standardSubject={standardCourse.standardName}
                              currentModule={module.moduleName}
                              selected={selected}
                              addLecture={setSelected}
                              lectureOptions={standardCourse.lectureOptions}
                              dense
                            />
                          ) : (
                            <SubjectCard
                              title={standardCourse.lectureOptions[0].courseName}
                              courseId={standardCourse.lectureOptions[0].courseId}
                              credit={standardCourse.lectureOptions[0].credit}
                              currentModule={module.moduleName}
                              selected={selected}
                              addLecture={setSelected}
                              isSelectable={standardCourse.lectureOptions[0].isSelectable}
                              dense
                            />
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ width: '100%', py: 4, textAlign: 'center' }}>
            <Typography>{isLoading ? '데이터를 불러오는 중입니다...' : '표시할 과목이 없습니다.'}</Typography>
          </Box>
        )}
      </Box>

      <Dialog open={modalOpen} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{'모듈 발급 신청 완료'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#272727', fontSize: '1rem', margin: '5px', mt: '20px' }}>
            모듈 발급 신청이 완료 되었습니다
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleModalClose}>
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyTable;
